import Config   from './provider/config'
import Modules  from './provider/modules'

import coerce   from './methods/coerce'
import generate from './helpers/generate'

import { isArray, isObject, isFunction, hasProp } from 'vessel/utils'
import { deepIterate, camelize, hyphenate } from 'vessel/utils'

const screens   = ['all', ...Object.keys(Config.screens)]
const states    = ['normal', 'hover', 'focus', 'active', 'group-hover', 'groupHover']
const styles    = Object.keys(Modules)

const isScreen  = val => screens.includes(val) && 'screen'
const getScreen = val => val.find(i => screens.includes(i))

const isState   = val => states.includes(val) && 'state'
const getState  = val => val.find(i => states.includes(i))

const isStyle   = val => styles.includes(val) && 'style'
const getStyle  = val => val.find(i => styles.includes(i))

const isModule  = val => isScreen(val) || isState(val) || isStyle(val)
const isDollar  = val => val && val.startsWith('$') && 'props'


function attrVariants(name) {
  return [camelize(name), hyphenate(name), name.toLowerCase()]
}

function contextAttrs(context, allowed) {
  if (allowed) {
    const names = Object.keys(context.$attrs || {}).map(camelize)
    const valid = name => allowed.includes('all') || allowed.includes(name)

    return names.filter(name => isStyle(name) && valid(name))
  } else {
    return []
  }
}

class VesselNode {
  constructor({ source, name, prop, value, screen, state, context }, type = 'node') {
    this.source   = source || type
    this.name     = name
    this.prop     = prop   || name
    this.rawValue = value
    this.screen   = screen || 'all'
    this.state    = state  || 'normal'
    this.context  = context
  }

  get priority() {
    const { source } = this
    const orders = { props: 0, attrs: 100, style: 200 }

    return orders[source]
  }

  get path() {
    const { screen, state, name } = this
    return [screen, state, name].join(':')
  }

  get value() {
    return this.rawValue
  }

  get variants() {
    const { name } = this
    return attrVariants(name)
  }

  get config() {
    const { name } = this
    return hasProp(name, Modules) && Modules[name]
  }

  get children() {
    const { value, name } = this

    if (isObject(value)) {
      return value
    } else if (isArray(value)) {
      console.info('Deprecated Vessel value:', { name, value })
    }

    return false
  }

  get classValue() {
    const { value, config: { coerce } } = this
    return coerce.call(null, value, Config)
  }
}

class VesselStyle extends VesselNode {
  constructor(options) {
    super(options, 'style')
  }

  get value() {
    const { name, styleValue } = this

    if (isObject(styleValue)) {
      return { [name]: styleValue }
    } else {
      return styleValue
    }
  }

  get styleValue() {
    const { context, rawValue } = this

    if (isFunction(rawValue)) {
      return rawValue.call(context)
    } else {
      return rawValue
    }
  }
}

class VesselProp extends VesselNode {
  constructor(options) {
    super(options, 'props')
  }

  get value() {
    const { name, coercedValue } = this

    if (isObject(coercedValue)) {
      return { [name]: coercedValue }
    } else {
      return coercedValue
    }
  }

  get propName() {
    return this.prop.replace(/^\$/, '')
  }

  get propValue() {
    return this.context[this.propName]
  }

  get coercedValue() {
    const { context, propValue, rawValue } = this

    if (isFunction(rawValue)) {
      return coerce(propValue, rawValue.bind(context))
    } else {
      return rawValue
    }
  }
}

class VesselAttr extends VesselNode {
  constructor(options) {
    super(options, 'attrs')
  }

  get value() {
    const { name, attrValue } = this

    if (isObject(attrValue)) {
      return { [name]: attrValue }
    } else {
      return attrValue
    }
  }

  get attrValue() {
    const { context, variants, rawValue } = this

    if (rawValue) {
      return rawValue
    } else {
      const attrs = context.$attrs || {}
      const name  = Object.keys(attrs).find(attr => variants.includes(attr))

      return attrs[name]
    }
  }
}

class VesselGroup {
  constructor() {
    this.modules = []
  }

  get module() {
    const { modules } = this

    if (modules.length > 1) {
      const valid = val => val !== null && val !== undefined
      const value = modules.find(({ classValue }) => valid(classValue))

      return value || modules[0]
    } else {
      return modules[0]
    }
  }

  get classes() {
    const { screen, state, name, classValue } = this.module
    return generate(screen, state, name, classValue)
  }

  addModule(module) {
    const { modules } = this
    const { priority } = module

    module.index = priority + modules.length + 1

    modules.push(module)
    modules.sort((a, b) => a.index > b.index)
  }
}

export class VesselObject {
  constructor({ context, attribs, modules }) {
    this.context = context
    this.attribs = attribs && [].concat(attribs)
    this.modules = []

    this.collectAttribs()
    this.collectModules(modules)
  }

  get attrs() {
    const { attribs } = this
    return contextAttrs(this.context, attribs)
  }

  get groups() {
    const { modules } = this

    return modules.reduce((result, module) => {
      const { path } = module

      const group = result[path] || new VesselGroup()
      group.addModule(module)

      result[path] = group
      return result
    }, {})
  }

  get classes() {
    const { groups } = this

    return Object.values(groups).reduce((result, { classes }) => {
      result = { ...result, ...classes }
      return result
    }, {})
  }

  collectAttribs() {
    this.attrs.forEach(key => {
      this.addAttr({ key, path: [], value: null })
    })
  }

  collectModules(modules, parent) {
    const isProp = keys => keys.some(isDollar)

    deepIterate(modules, (key, value, path) => {
      const prop = path.find(isDollar) || key
      const keys = [key, prop]
      const opts = { key, value, path }

      if (isProp(keys)) {
        this.addProp(prop, opts)
      } else {
        this.addStyle(parent, opts)
      }
    })
  }

  addProp(prop, options) {
    const { context } = this
    const { value } = options

    const createProp = config => {
      return new VesselProp({ ...config, context, prop })
    }

    if (isFunction(value)) {
      this.addModule(options, createProp)
    } else if (value) {
      const modules = [].concat(value)

      modules.forEach(key => {
        const opts = { ...options, key, value: val => val }
        this.addModule(opts, createProp)
      })
    }
  }

  addStyle(prop, options) {
    const { context, attrs } = this

    const isAttr = name => name && attrs.includes(name) && 'attrs'
    const source = isAttr(prop) || isDollar(prop) || 'style'

    this.addModule(options, (config) => {
      return new VesselStyle({ ...config, source, context, prop })
    })
  }

  addAttr(options) {
    const { context } = this

    this.addModule(options, (config) => {
      return new VesselAttr({ ...config, context })
    })
  }

  addModule({ key, value, path }, callback) {
    const options = { name: null, screen: null, state: null, value }

    switch (isModule(key)) {
      case 'screen':
        options.name   = getStyle(path)
        options.screen = key
      break
      case 'state':
        options.name   = getStyle(path)
        options.screen = getScreen(path)
        options.state  = key
      break
      case 'style':
        options.name   = key
        options.screen = getScreen(path)
        options.state  = getState(path)
      break
    }

    const node = callback.call(null, options)
    const { name } = options

    if (isStyle(name)) {
      const { prop, children } = node

      if (children) {
        this.collectModules(children, prop)
      } else {
        this.modules.push(node)
      }
    } else {
      const source = { key, value, path }
      console.info('Invalid Vessel style:', { source, node })
    }
  }
}

export class VesselOptions {
  constructor({ context, options }) {
    this.context = context || {}
    this.options = {}
    this.attribs = []

    Object.keys(options).forEach(key => {
      this.options[key] = this.normalize(options, options[key])
    })

    Object.values(options).forEach(({ $attrs }) => {
      this.attribs = this.attribs.concat($attrs || [])
    })
  }

  get attrs() {
    const { attribs } = this

    const names = contextAttrs(this.context, attribs)
    const values = names.map(attrVariants)

    return new Set(values.flat())
  }

  get objects() {
    const { options } = this

    return Object.keys(options).reduce((result, key) => {
      result[key] = this.object(key)
      return result
    }, {})
  }

  get vessels() {
    const { options } = this

    return Object.keys(options).reduce((result, key) => {
      result[key] = this.vessel(key)
      return result
    }, {})
  }

  object(name) {
    const { options } = this
    const { $attrs, ...modules } = options[name]

    return new VesselObject({
      context: this.context,
      attribs: $attrs,
      modules: modules
    })
  }

  vessel(name) {
    const object = this.object(name)
    return object.classes
  }

  normalize(options, value) {
    let { $extend, $extends, $attrs, ...modules } = value
    const extend = [].concat($extend || $extends)

    extend.forEach(other => {
      const otherVal = options[other]

      if (otherVal) {
        const otherOpt = this.normalize(options, otherVal)
        modules = { ...otherOpt, ...modules }
      }
    })

    return { $attrs, ...modules }
  }
}
